import React, { useEffect } from 'react';
import { useConfiguration } from '../../context/configurationContext';
import loadable from '@loadable/component';

const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

// The blockId of the legal-links column as configured in Sharetribe Console.
const LEGAL_BLOCK_ID = 'terms-and-privacy';

// Exact markdown line appended to the legal column. Used for the idempotency check.
const COOKIE_LINK_LINE = '- [Настройки cookies](#cookie-settings)';

/**
 * Returns a shallow-cloned footer object where the legal-links block's markdown
 * content has `COOKIE_LINK_LINE` appended, if not already present.
 * Does not mutate the original footer object from useConfiguration().
 * Future Console-managed links added to the block appear before this line.
 */
const augmentFooterWithCookieLink = footer => {
  const blocks = footer.blocks || [];
  if (!blocks.some(b => b.blockId === LEGAL_BLOCK_ID)) {
    return footer;
  }

  return {
    ...footer,
    blocks: blocks.map(block => {
      if (block.blockId !== LEGAL_BLOCK_ID) {
        return block;
      }
      const content = block.text?.content || '';
      if (content.includes(COOKIE_LINK_LINE)) {
        return block;
      }
      return {
        ...block,
        text: {
          ...block.text,
          content: content + '\n' + COOKIE_LINK_LINE,
        },
      };
    }),
  };
};

const FooterComponent = () => {
  const { footer = {}, topbar } = useConfiguration();

  // Native capture-phase listener on document intercepts the click before the browser
  // resolves the href for navigation, making preventDefault() effective.
  // React synthetic events (onClick / onClickCapture) fire too late for hash-anchor
  // navigation interception, which is why we use the native API here.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const handleDocumentClick = e => {
      const link = e.target.closest?.('a');
      if (!link) {
        return;
      }

      const hrefAttr = link.getAttribute('href');
      const isCookieSettingsLink =
        hrefAttr === '#cookie-settings' ||
        hrefAttr === '/#cookie-settings' ||
        link.hash === '#cookie-settings' ||
        (typeof link.href === 'string' && link.href.endsWith('#cookie-settings'));

      if (!isCookieSettingsLink) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('delowo:openCookieNotice'));
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, []);

  // If footer asset is not set, let's not render Footer at all.
  if (Object.keys(footer).length === 0) {
    return null;
  }

  const augmentedFooter = augmentFooterWithCookieLink(footer);

  // The footer asset does not specify sectionId or sectionType. However, the SectionBuilder
  // expects sectionId and sectionType in order to identify the section. We add those
  // attributes here before passing the asset to SectionBuilder.
  const footerSection = {
    ...augmentedFooter,
    sectionId: 'footer',
    sectionType: 'footer',
    linkLogoToExternalSite: topbar?.logoLink,
  };

  return <SectionBuilder sections={[footerSection]} />;
};

// NOTE: if you want to add dynamic data to FooterComponent,
//       you could just connect this FooterContainer to Redux Store
//
// const mapStateToProps = state => {
//   const { currentUser } = state.user;
//   return { currentUser };
// };
// const FooterContainer = compose(connect(mapStateToProps))(FooterComponent);
// export default FooterContainer;

export default FooterComponent;
